import { Cart as CartType } from '../helpers/cart.types'

export class ConditionalCartContentElement extends HTMLElement {
  private static savedContent: Map<string, string> = new Map()
  private static CART_EVENTS = ['renderCartPage', 'renderMiniCart']

  private cartCondition: 'empty' | 'not_empty' | 'below_threshold' = 'empty'
  private cartCount: number = 1
  private threshold: number = 0
  private updateTimeout: NodeJS.Timeout | null = null
  private cartUpdateHandler: (() => void) | null = null
  private isCurrentlyVisible: boolean | null = null
  private lastItemCount: number | null = null
  private lastTotalPrice: number | null = null

  connectedCallback() {
    this.parseAttributes()
    this.restoreContentIfEmpty()
    this.storeOriginalContent()
    this.subscribeToCartUpdates()
    this.checkInitialCartState()
  }

  disconnectedCallback() {
    this.unsubscribeFromCartUpdates()
  }

  private parseAttributes() {
    const condition = this.getAttribute('data-cart-condition')
    if (condition === 'empty' || condition === 'not_empty' || condition === 'below_threshold') {
      this.cartCondition = condition
    }

    const count = this.getAttribute('data-cart-count')
    if (count) {
      this.cartCount = parseInt(count, 10) || 1
    }

    const threshold = this.getAttribute('data-threshold')
    if (threshold) {
      this.threshold = parseInt(threshold, 10) || 0
    }
  }

  private restoreContentIfEmpty() {
    const key = this.getAttribute('data-content-key')
    if (!key) return
    if (!this.innerHTML.trim() && ConditionalCartContentElement.savedContent.has(key)) {
      this.innerHTML = ConditionalCartContentElement.savedContent.get(key)!
    }
  }

  private storeOriginalContent() {
    const key = this.getAttribute('data-content-key')
    if (key && this.innerHTML.trim()) {
      ConditionalCartContentElement.savedContent.set(key, this.innerHTML)
    }
  }

  private subscribeToCartUpdates() {
    this.cartUpdateHandler = () => {
      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout)
      }

      this.updateTimeout = setTimeout(() => {
        if (!window.Cart) return

        window.Cart.getCart()
          .then(() => {
            if (window.Cart?.value) this.updateVisibility()
          })
          .catch(() => {})
      }, 300)
    }

    ConditionalCartContentElement.CART_EVENTS.forEach((event) => {
      document.addEventListener(event, this.cartUpdateHandler!)
    })
  }

  private unsubscribeFromCartUpdates() {
    if (this.cartUpdateHandler) {
      ConditionalCartContentElement.CART_EVENTS.forEach((event) => {
        document.removeEventListener(event, this.cartUpdateHandler!)
      })
    }

    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout)
      this.updateTimeout = null
    }
  }

  private checkInitialCartState() {
    if (window.Cart?.value) {
      this.updateVisibility()
      return
    }

    const globalCart = (window as any).globals?.cart
    if (globalCart?.initCart) {
      this.evaluateCondition(globalCart.initCart)
      return
    }

    if (window.Cart) {
      window.Cart.getCart()
        .then(() => {
          this.updateVisibility()
        })
        .catch(() => {
          this.hideContent()
        })
    } else {
      this.hideContent()
    }
  }

  private updateVisibility() {
    const cart = window.Cart?.value as CartType | undefined
    if (!cart) {
      const globalCart = (window as any).globals?.cart
      if (globalCart?.initCart) {
        this.evaluateCondition(globalCart.initCart)
        return
      }
      this.hideContent()
      return
    }

    this.evaluateCondition(cart)
  }

  private evaluateCondition(cart: CartType | any) {
    const itemCount = cart.item_count || 0
    const totalPrice = cart.total_price || 0
    let shouldShow = false

    if (this.cartCondition === 'empty') {
      shouldShow = itemCount === 0
    } else if (this.cartCondition === 'not_empty') {
      shouldShow = itemCount >= this.cartCount
    } else if (this.cartCondition === 'below_threshold') {
      shouldShow = totalPrice < this.threshold && itemCount > 0
    }

    if (this.isCurrentlyVisible === shouldShow && this.lastItemCount === itemCount && this.lastTotalPrice === totalPrice) {
      return
    }

    this.lastItemCount = itemCount
    this.lastTotalPrice = totalPrice

    if (shouldShow) {
      this.showContent()
    } else {
      this.hideContent()
    }
  }

  private showContent() {
    if (this.isCurrentlyVisible === true) {
      return
    }

    this.style.display = ''
    this.style.visibility = ''
    this.removeAttribute('hidden')
    this.isCurrentlyVisible = true

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.reinitializeNestedComponents()
      })
    })
  }

  private reinitializeNestedComponents() {
    setTimeout(() => {
      this.querySelectorAll('*').forEach((element: any) => {
        try {
          if (element.flickity) {
            element.flickity.resize()
            element.flickity.reloadCells()
          } else if (window.Flickity && (window as any).Flickity.data) {
            const instance = (window as any).Flickity.data(element)
            if (instance) {
              instance.resize()
              instance.reloadCells()
            }
          }
        } catch {}
      })

      this.dispatchEvent(
        new CustomEvent('conditional-cart-content:visible', {
          bubbles: true,
          detail: { element: this }
        })
      )
      window.dispatchEvent(new Event('resize'))
    }, 50)
  }

  private hideContent() {
    if (this.isCurrentlyVisible === false) {
      return
    }

    this.style.display = 'none'
    this.isCurrentlyVisible = false
  }
}
